import { useState, useRef, useEffect } from 'react';
import { Flex, Select } from '@mantine/core';
import { FilterControlsWrapper, SelectWrapper } from './styles';
import { useIntl } from 'react-intl';
import { SearchAutoComplete } from '@jadwalh/ui';
import { useSelector } from 'react-redux';
import { getProfileData } from '../../../../store/profile.slice';
import { useSearchParams } from 'react-router-dom';
import { MdKeyboardArrowDown } from 'react-icons/md';
import { useQuery } from '@tanstack/react-query';
import instance from '../../../../axios-instance';

export default function FilterControls() {
  const { formatMessage } = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();
  const [eventWidth, setEventWidth] = useState<number>(87);
  const hiddenEventSpanRef = useRef<HTMLSpanElement>(null);
  const [selectedEvent, setSelectedEvent] = useState('');
  const selectRef = useRef<any>(null);
  const profile = useSelector(getProfileData);

  const { data: eventOptions } = useQuery({
    queryKey: ['events'],
    queryFn: () => instance.get(`/app/list/events?client_id=${profile.id}&status=1&scope=general&category=event`),
    select: (res: any): { value: string; label: string }[] =>
      res.data.data.map((item: any) => ({
        value: item.value.toString(),
        label: item.label,
      })),
    cacheTime: 0,
  });

  const getEventLabel = (val: string | undefined) =>
    eventOptions?.find((item: any) => item.value === val)?.label || '';

  const scrollToSelectedValue = () => {
    if (selectedEvent) {
      // Try immediate scroll first
      const tryScroll = () => {
        const dropdown =
          document.querySelector('.listOptions-events-dropdwon') ||
          document.querySelector('.mantine-Select-dropdown') ||
          document.querySelector('.mantine-Popover-dropdown');

        if (dropdown) {
          // Find the actual scrollable container within the dropdown
          const scrollableContainer =
            dropdown.querySelector('.mantine-Select-itemsWrapper') ||
            dropdown.querySelector('.mantine-Select-list') ||
            dropdown.querySelector('[data-mantine-scroll-area-viewport]') ||
            dropdown.querySelector('.mantine-ScrollArea-viewport') ||
            dropdown;

          const selectedItem =
            dropdown.querySelector(`[data-value="${selectedEvent}"]`) ||
            dropdown.querySelector(`[data-selected="true"]`);

          if (selectedItem) {
            const selectedIndex = eventOptions?.findIndex(
              (option) => option.value === selectedEvent
            );
            const isLastItem =
              selectedIndex !== -1 &&
              selectedIndex === (eventOptions?.length || 0) - 1;

            if (isLastItem) {
              // Try to find the parent container that might be scrollable
              let scrollableParent = selectedItem.parentElement;
              while (scrollableParent && scrollableParent !== document.body) {
                const style = window.getComputedStyle(scrollableParent);
                if (
                  style.overflow === 'auto' ||
                  style.overflow === 'scroll' ||
                  style.overflowY === 'auto' ||
                  style.overflowY === 'scroll'
                ) {
                  // Try to scroll this parent
                  const itemOffsetTop = (selectedItem as HTMLElement).offsetTop;
                  const parentHeight = scrollableParent.clientHeight;
                  const itemHeight = (selectedItem as HTMLElement).offsetHeight;

                  // Position item at bottom of parent with some padding
                  const targetScrollTop =
                    itemOffsetTop - parentHeight + itemHeight + 50;

                  scrollableParent.scrollTo({
                    top: Math.max(0, targetScrollTop),
                    behavior: 'smooth',
                  });
                  break;
                }
                scrollableParent = scrollableParent.parentElement;
              }

              // No fallback to avoid conflicts
            } else {
              selectedItem.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
              });
            }
            return true; // Success
          }
        }
        return false; // Not found yet
      };

      // Try immediately
      if (!tryScroll()) {
        // If not found, try with delays
        setTimeout(() => tryScroll(), 100);
        setTimeout(() => tryScroll(), 300);
        setTimeout(() => tryScroll(), 600);
        setTimeout(() => tryScroll(), 1000);
      }
    }
  };

  useEffect(() => {
    const currentEvent = document.getElementById('currentEvent');
    if (hiddenEventSpanRef.current) {
      setEventWidth(hiddenEventSpanRef.current.offsetWidth + 40);
    } else if (currentEvent) {
      setEventWidth(currentEvent.offsetWidth + 40);
    } else {
      setEventWidth(87);
    }
  }, [selectedEvent, eventOptions]);

  useEffect(() => {
    if (searchParams.get('event')) {
      setSelectedEvent(searchParams.get('event') || '');
    } else {
      setSelectedEvent('');
    }
  }, [searchParams]);

  return (
    <FilterControlsWrapper>
      <Flex
        align="center"
        className="search-container"
        justify="flex-start"
        gap={'12px'}
        direction={{ base: 'row', md: 'row' }}
      >
        <Flex gap={'12px'}>
          <SelectWrapper>
            {selectedEvent && (
              <span
                ref={hiddenEventSpanRef}
                id="currentEvent"
                style={{
                  position: 'absolute',
                  visibility: 'hidden',
                  whiteSpace: 'nowrap',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  fontWeight: 400,
                }}
              >
                {getEventLabel(selectedEvent)}
              </span>
            )}
            <Select
              ref={selectRef}
              withinPortal={true}
              name="event"
              value={selectedEvent}
              onChange={(value) => {
                if (value === 'clear') {
                  searchParams.delete('event');
                  setSearchParams(searchParams);
                  setSelectedEvent('');
                } else if (value) {
                  searchParams.set('event', value);
                  setSearchParams(searchParams);
                  setSelectedEvent(value);
                }
              }}
              onDropdownOpen={() => {
                // Single scroll attempt with delay to ensure dropdown is rendered
                setTimeout(() => {
                  scrollToSelectedValue();
                }, 100);
              }}
              className="listOptions"
              classNames={{
                dropdown: 'listOptions-events-dropdwon',
              }}
              data={
                eventOptions && eventOptions.length > 0
                  ? [
                      ...eventOptions,
                      ...(selectedEvent
                        ? [
                            {
                              value: 'clear',
                              label:
                                formatMessage({
                                  id: 'deleteTxt',
                                }) || 'Clear',
                              className: 'clear-option',
                            },
                          ]
                        : []),
                    ]
                  : [
                      {
                        label: formatMessage({
                          id: 'noEvents',
                        }),
                        value: '0',
                        disabled: true,
                      },
                    ]
              }
              icon={<MdKeyboardArrowDown />}
              placeholder={formatMessage({
                id: 'theService',
              })}
              rightSection={''}
              searchable={false}
              styles={() => ({
                wrapper: {
                  width: eventWidth || 87,
                  height: 30,
                  maxWidth: eventWidth || 87,
                },
                item: {
                  wordBreak: 'normal',
                  textAlign:
                    localStorage.getItem('lang') === 'ar' ? 'right' : 'left',
                  '&[data-selected="true"]': {
                    backgroundColor: '#000',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: '#000',
                    },
                  },
                },
              })}
            />
          </SelectWrapper>
          {/* <SelectWrapper>
            <Select
              name="status"
              data={[
                {
                  value: '0',
                  label: formatMessage({ id: 'inactive' }),
                },
                {
                  value: '1',
                  label: formatMessage({ id: 'active' }),
                },
                ...(selectedStatus
                  ? [
                      {
                        value: 'clear',
                        label: formatMessage({ id: 'deleteTxt' }) || 'Clear',
                        className: 'clear-option',
                      },
                    ]
                  : []),
              ]}
              onChange={(value) => {
                if (value === 'clear') {
                  searchParams.delete('status');
                  setSelectedStatus('');
                  setSearchParams(searchParams);
                } else if (value) {
                  searchParams.set('status', value);
                  setSelectedStatus(value);
                  setSearchParams(searchParams);
                }
              }}
              placeholder={formatMessage({
                id: 'Choose Status',
              })}
              value={selectedStatus}
              rightSection={<div />}
              icon={<MdKeyboardArrowDown />}
              className="status-options"
              styles={() => ({
                wrapper: {
                  width: 62,
                  height: 30,
                  maxWidth: 62,
                  textOverflow: 'ellipsis',
                },
                item: {
                  wordBreak: 'normal',
                  textAlign:
                    localStorage.getItem('lang') === 'ar' ? 'right' : 'left',
                  '&[data-selected="true"]': {
                    backgroundColor: '#000',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: '#000',
                    },
                  },
                },
              })}
            />
          </SelectWrapper> */}
        </Flex>
        <div className="searchWrapper">
        <SearchAutoComplete
          searchKey="name"
          placeholder="workflowName"
          data={['test']}
        />
        </div>
      </Flex>
    </FilterControlsWrapper>
  );
}
